import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { getVariantPurchaseState, getPurchaseButtonTranslationKey } from '@theme/variant-purchase-state';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, { signal });

    // Theme proxy CTA (Pre-order / Notify me) → forward the click to the hidden app button.
    // Delegate so wiring survives morph / late ref updates (direct bind on the button dies
    // when the node is replaced without reconnecting this component).
    this.addEventListener('click', this.#onAppProxyClick, { signal });

    // Sync the proxy label from the app button once it injects (app loads late).
    this.#observeAppButtons();

    // Re-assert CTA from current variant immediately (avoids Add-to-bag → Pre-order flash).
    queueMicrotask(() => {
      const selected = this.#resolveSelectedVariantFromPicker();
      if (!selected) return;
      const purchaseState = getVariantPurchaseState(
        selected,
        this.closest('.shopify-section') || this
      );
      this.#syncAppCta(purchaseState, { pending: false });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#appButtonObserver?.disconnect();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, variantId } = this.refs;
    
    // CRITICAL: Check if the submit event came from variant picker form (should not submit product form)
    const variantPicker = event.target.closest('variant-picker');
    if (variantPicker) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    const quantity = this.#resolveQuantityValue();
    this.#applyQuantityToForms(quantity);

    // Use the add-to-cart form (not the nested accelerated-checkout payment form).
    const form =
      (event.target instanceof HTMLFormElement && event.target.matches('[data-type="add-to-cart-form"]')
        ? event.target
        : null) ?? this.#getAddToCartForm();

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    formData.set('quantity', String(quantity));

    if (variantId?.value) {
      formData.set('id', variantId.value);
    }

    // Verify the variant ID is present
    const variantIdFromForm = formData.get('id');
    if (!variantIdFromForm || variantIdFromForm === '') {
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        // Expect JSON so we can parse the response and dispatch cart events
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch(() => {
        const message =
          Theme.translations?.add_to_cart_error ||
          "Couldn't add to bag";
        window.dispatchEvent(new CartErrorEvent(this.id, message));
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(message));
          }
        }
        this.#setLiveRegionText(message);
        this.#timeout = setTimeout(() => {
          if (addToCartTextError) addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 10000);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getAddToCartForm() {
    return this.querySelector('form[data-type="add-to-cart-form"]');
  }

  /**
   * Reads the quantity the customer selected from any selector in this component or section.
   * @returns {number}
   */
  #resolveQuantityValue() {
    const section = this.closest('.product-information[data-section-id], .product-information.section, .shopify-section');
    const candidates = section
      ? Array.from(
          section.querySelectorAll(
            'product-form-component input[name="quantity"], .product-sticky-bar input[name="quantity"]'
          )
        )
      : Array.from(this.querySelectorAll('input[name="quantity"]'));

    const enabled = candidates.filter((input) => input instanceof HTMLInputElement && !input.disabled);
    const visible =
      enabled.find((input) => {
        const rect = input.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) ?? enabled[0];

    if (visible instanceof HTMLInputElement) {
      const qty = parseInt(visible.value, 10);
      if (!isNaN(qty) && qty > 0) return qty;
    }

    return Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Keeps add-to-cart and accelerated-checkout forms aligned with the selected quantity.
   * @param {number} quantity
   */
  #applyQuantityToForms(quantity) {
    const qty = String(Math.max(1, quantity));
    const variantId = this.refs.variantId?.value;

    this.querySelectorAll('input[name="quantity"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.disabled) return;
      input.value = qty;
    });

    this.querySelectorAll('.accelerated-checkout-block form').forEach((form) => {
      let quantityInput = form.querySelector('input[name="quantity"]');
      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.setAttribute('data-payment-quantity', '');
        form.prepend(quantityInput);
      }
      quantityInput.value = qty;

      if (variantId) {
        let idInput = form.querySelector('input[name="id"]');
        if (!idInput) {
          idInput = document.createElement('input');
          idInput.type = 'hidden';
          idInput.name = 'id';
          idInput.setAttribute('data-payment-variant-id', '');
          form.prepend(idInput);
        }
        idInput.value = variantId;
      }
    });
  }

  /**
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (event.detail?.cartLine) return;

    const section = this.closest('.product-information[data-section-id], .product-information.section, .shopify-section');
    if (!section?.contains(event.target)) return;

    const quantity = Number(event.detail?.quantity);
    if (!quantity || quantity < 1) return;

    section.querySelectorAll('product-form-component').forEach((component) => {
      if (component instanceof ProductFormComponent) {
        component.#applyQuantityToForms(quantity);
      }
    });
  };

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {Document} htmlDoc
   * @returns {HTMLElement | null}
   */
  #getMorphAddToCartSource(htmlDoc) {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (form?.id) {
      const scoped = htmlDoc.querySelector(`form#${CSS.escape(form.id)} [ref="addToCartButton"]`);
      if (scoped instanceof HTMLElement) return scoped;
    }
    const scopedButton = htmlDoc.querySelector('[ref="addToCartButton"]');
    return scopedButton instanceof HTMLElement ? scopedButton : null;
  }

  /**
   * @param {{ mode: string, canAddToCart?: boolean }} purchaseState
   */
  #getPurchaseButtonFallbackLabel(purchaseState) {
    if (
      purchaseState.mode === 'sold_out' ||
      purchaseState.mode === 'notify' ||
      purchaseState.mode === 'pre_order'
    ) {
      return Theme.translations?.sold_out || 'Sold out';
    }
    const key = getPurchaseButtonTranslationKey(purchaseState.mode);
    if (key === 'unavailable') return Theme.translations?.unavailable || 'Unavailable';
    if (key === 'sold_out') return Theme.translations?.sold_out || 'Sold out';

    // Prefer the live ATC label when it already reads as Add-to-bag (locale-safe).
    const btn = this.refs.addToCartButtonContainer?.refs?.addToCartButton;
    const current = (
      btn?.querySelector?.('.add-to-cart-text__content') ||
      btn?.querySelector?.('.add-to-cart-text')
    )?.textContent
      ?.replace(/\s+/g, ' ')
      .trim();
    if (
      current &&
      /add|ajouter|bag|cart|panier/i.test(current) &&
      !/sold|notify|pre-?\s?order|épuisé|indisponible/i.test(current)
    ) {
      return current;
    }
    return Theme.translations?.add_to_cart || 'Add to bag';
  }

  /** @type {MutationObserver | undefined} */
  #appButtonObserver;

  /**
   * Theme owns the visible CTA. OOS → grayed Sold out on the ATC button.
   * Notify Me / Pre-order app proxies are unused (wishlist mount remains).
   *
   * @param {{ mode: string, canAddToCart?: boolean }} purchaseState
   * @param {{ pending?: boolean }} [options]
   */
  #syncAppCta(purchaseState, options = {}) {
    const block = this.closest('.buy-buttons-block');
    if (!(block instanceof HTMLElement)) return;

    block.classList.remove(
      'buy-buttons-block--app-cta',
      'buy-buttons-block--app-notify',
      'buy-buttons-block--app-preorder',
      'buy-buttons-block--variant-pending'
    );
    block.removeAttribute('data-app-notify');
    block.removeAttribute('data-app-preorder');
    this.#updateProxyButton('none');

    const mode = purchaseState?.mode;
    const soldOut =
      mode === 'sold_out' || mode === 'unavailable' || mode === 'pre_order' || mode === 'notify';
    const canAdd = Boolean(purchaseState?.canAddToCart) && mode === 'add_to_cart';

    if (soldOut || !canAdd) {
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      this.refs.addToCartButtonContainer?.disable?.();
      const btn = this.refs.addToCartButton;
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        const label =
          btn.querySelector('.add-to-cart-text__content') || btn.querySelector('.add-to-cart-text');
        if (label) {
          label.textContent =
            mode === 'unavailable'
              ? Theme.translations?.unavailable || 'Unavailable'
              : Theme.translations?.sold_out || 'Sold out';
        }
      }
      return;
    }

    this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    this.refs.addToCartButtonContainer?.enable?.();
    const btn = this.refs.addToCartButton;
    if (btn instanceof HTMLButtonElement) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      const label =
        btn.querySelector('.add-to-cart-text__content') || btn.querySelector('.add-to-cart-text');
      if (label) {
        label.textContent = this.#getPurchaseButtonFallbackLabel(purchaseState);
      }
    }
  }

  /**
   * Show/label the theme proxy button for the given mode.
   * @param {'pre_order' | 'notify' | 'none'} mode
   */
  #updateProxyButton(mode) {
    const proxy = this.refs.appProxyCta;
    if (!(proxy instanceof HTMLElement)) return;

    const textEl = proxy.querySelector('.app-proxy-cta__text');

    if (mode === 'none') {
      proxy.setAttribute('hidden', '');
      proxy.dataset.mode = 'none';
      return;
    }

    proxy.removeAttribute('hidden');
    proxy.dataset.mode = mode;

    if (!textEl) return;

    if (mode === 'notify') {
      textEl.textContent =
        proxy.dataset.notifyLabel ||
        Theme.translations?.notify_me_when_available ||
        'Notify me when available';
    } else {
      // Only adopt the app label when it clearly reads as Pre-order — never let a
      // late-inject "Add to bag" flash overwrite the theme Pre-order CTA.
      const appLabel = this.#getAppButtonLabel('pre_order');
      const isPreorderLabel = /pre-?\s?order|preorder|précommande/i.test(appLabel || '');
      textEl.textContent =
        (isPreorderLabel && appLabel) ||
        proxy.dataset.preorderLabel ||
        Theme.translations?.pre_order ||
        'Pre-order';
    }
  }

  /** Proxy click → open the matching hidden app button. */
  #onAppProxyClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const proxy = target.closest('[data-app-proxy-cta], .app-proxy-cta');
    if (!(proxy instanceof HTMLElement) || !this.contains(proxy)) return;
    if (proxy.hasAttribute('hidden') || proxy.dataset.mode === 'none') return;

    event.preventDefault();
    event.stopPropagation();
    const mode = proxy.dataset.mode;
    if (mode === 'pre_order' || mode === 'notify') {
      this.#openAppCta(mode);
    }
  };

  /**
   * Public entry for sticky bars / other proxies — same path as the buy-box CTA.
   * @param {'pre_order' | 'notify'} [mode]
   * @returns {boolean}
   */
  openAppNotifyCta(mode = 'notify') {
    if (mode !== 'pre_order' && mode !== 'notify') mode = 'notify';
    return this.#openAppCta(mode);
  }

  /**
   * @param {'pre_order' | 'notify'} mode
   * @returns {HTMLElement | null}
   */
  #findAppButton(mode) {
    const scope = this.closest('.shopify-section') || document;
    const preorderSelectors = [
      '.restock-rocket-preorder-button',
      '.restock-rocket-button-product',
      '.restock-rocket-button',
      '[class*="restock-rocket-button"]',
      'a[class*="preorder"]',
      'button[class*="preorder"]',
      '[data-action="preorder"]',
    ];
    const notifySelectors = [
      '.restock-rocket-button-product',
      '.restock-rocket-button',
      '.restock-rocket-button-float',
      '[class*="restock-rocket-button"]',
      '[data-notify-me]',
      'button[class*="notify"]',
      'a[class*="notify"]',
    ];
    const selectors = mode === 'pre_order' ? preorderSelectors : notifySelectors;
    const isPreorderLabel = (t) => /pre-?\s?order|preorder|précommande/i.test(t || '');
    const isNotifyLabel = (t) =>
      /notify(\s*me)?|notif(?:y|ier)|restock|back\s*in\s*stock|m['’]?\s*avert|avertir|me\s+notifier/i.test(
        t || ''
      );

    const roots = [scope, document.body].filter(Boolean);
    const matches = [];
    for (const root of roots) {
      for (const sel of selectors) {
        try {
          root.querySelectorAll(sel).forEach((n) => {
            if (!(n instanceof HTMLElement) || n.id === 'form-Button') return;
            // Modal / inline form chrome only — inject CTAs can sit under body/float shells.
            if (
              n.closest(
                '#formContainer, #formContainer-inline, #preorderContainer, .stoq-preorder-modal'
              )
            ) {
              return;
            }
            if (n.matches?.('[data-app-proxy-cta], .app-proxy-cta') || n.closest?.('[data-app-proxy-cta], .app-proxy-cta')) {
              return;
            }
            const hay = `${n.className || ''} ${n.getAttribute('aria-label') || ''} ${n.getAttribute('title') || ''}`;
            if (/wishlist|souhaits/i.test(hay)) return;
            if (matches.indexOf(n) === -1) matches.push(n);
          });
        } catch (e) {
          /* ignore */
        }
      }
    }

    // Prefer a label match for the requested mode.
    for (const btn of matches) {
      const label = `${btn.textContent || ''} ${btn.getAttribute('aria-label') || ''}`;
      if (mode === 'pre_order' && isPreorderLabel(label)) return btn;
      if (mode === 'notify' && isNotifyLabel(label)) return btn;
    }

    // Never fall back to a random Restock node for preorder — that can be a
    // notify / ATC stub and overwrite the theme Pre-order label with "Add to bag".
    if (mode === 'pre_order') return null;

    return matches[0] || null;
  }

  /**
   * @param {'pre_order' | 'notify'} mode
   * @returns {string}
   */
  #getAppButtonLabel(mode) {
    const btn = this.#findAppButton(mode);
    if (!btn) return '';
    // Read only rendered text (skip hidden success states like "Added").
    const raw = (btn.textContent || '').replace(/\s+/g, ' ').trim();
    return raw.replace(/\s*\b(added(\s+to\s+(cart|bag))?)\s*$/i, '').trim() || raw;
  }

  /**
   * Open Notify Me! via the app JS API when no DOM button is available yet.
   * STOQ / Restock Rocket primary API is openModal(productData, variantId).
   * @returns {boolean}
   */
  #openNotifyViaApi() {
    const variantId = this.refs.variantId?.value;
    if (!variantId) return false;

    const api =
      /** @type {Record<string, Function> | null} */ (
        window._RestockRocket || window.RestockRocket || window.STOQ || window._STOQ || null
      );
    if (!api) return false;

    const productId = this.dataset.productId || '';

    try {
      if (typeof api.openModal === 'function') {
        try {
          api.openModal(null, String(variantId));
          return true;
        } catch (e1) {
          /* try next signature */
        }
        try {
          api.openModal(productId ? { id: productId } : null, String(variantId));
          return true;
        } catch (e2) {
          /* try next */
        }
        try {
          api.openModal(String(variantId));
          return true;
        } catch (e3) {
          /* fall through */
        }
      }
      if (typeof api.openInlineForm === 'function') {
        api.openInlineForm(null, String(variantId));
        return true;
      }
      if (typeof api.openForm === 'function') {
        api.openForm(null, String(variantId));
        return true;
      }
      if (typeof api.open === 'function') {
        api.open(String(variantId));
        return true;
      }
      if (typeof api.showForm === 'function') {
        api.showForm(String(variantId));
        return true;
      }
      if (typeof api.renderButtonForVariant === 'function') {
        api.renderButtonForVariant(String(variantId));
        // App may inject the button asynchronously — try one click after a short wait.
        window.setTimeout(() => {
          const btn = this.#findAppButton('notify');
          if (!(btn instanceof HTMLElement)) return;
          try {
            btn.click();
          } catch (e) {
            /* best-effort */
          }
        }, 150);
        return true;
      }
    } catch (e) {
      return false;
    }

    return false;
  }

  /**
   * Proxy-click the hidden app button. If the button is not in the DOM yet
   * (common while Notify Me! is still injecting), fall back to the app API
   * for notify — same pattern as the sticky bars.
   * @param {'pre_order' | 'notify'} mode
   * @returns {boolean}
   */
  #openAppCta(mode) {
    const btn = this.#findAppButton(mode);

    if (btn instanceof HTMLElement) {
      try {
        btn.focus({ preventScroll: true });
      } catch (e) {
        /* ignore */
      }
      // Single programmatic click (INP) — multi-event sequences were redundant with .click().
      if (typeof btn.click === 'function') {
        btn.click();
      } else {
        btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }
      // If the inject is parked/broken, still open via API for notify.
      if (mode === 'notify') {
        window.setTimeout(() => {
          const modalOpen = document.querySelector(
            '#formContainer:not([hidden]), .restock-rocket-wrapper:not([hidden]), #formContainer-inline:not([hidden])'
          );
          if (!modalOpen) this.#openNotifyViaApi();
        }, 200);
      }
      return true;
    }

    // No DOM button — notify can still open via the app API.
    if (mode === 'notify') {
      return this.#openNotifyViaApi();
    }

    return false;
  }

  /** Park app inject CTAs that re-apply visible inline styles after load. */
  #parkAppNotifyInjects() {
    const parkProps = {
      position: 'fixed',
      left: '-100vw',
      top: '0',
      right: 'auto',
      bottom: 'auto',
      width: '1px',
      height: '1px',
      'max-width': '1px',
      'max-height': '1px',
      'min-width': '0',
      'min-height': '0',
      margin: '0',
      padding: '0',
      opacity: '0',
      visibility: 'hidden',
      overflow: 'hidden',
      'clip-path': 'inset(50%)',
      transform: 'scale(0)',
      'z-index': '-1',
      'pointer-events': 'auto',
      flex: '0 0 0',
    };

    const applyPark = (node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.id === 'form-Button') return;
      if (
        node.closest(
          '#formContainer, #formContainer-inline, #preorderContainer, .stoq-preorder-modal'
        )
      ) {
        return;
      }
      if (node.matches?.('[data-app-proxy-cta], .app-proxy-cta') || node.closest?.('[data-app-proxy-cta], .app-proxy-cta')) {
        return;
      }
      const hay = `${node.className || ''} ${node.getAttribute('aria-label') || ''} ${
        node.getAttribute('title') || ''
      } ${String(node.textContent || '').slice(0, 80)}`;
      if (/wishlist|souhaits|add to wish|ajouter/i.test(hay) && !/notify|restock|pre-?\s?order/i.test(hay)) {
        return;
      }
      if (
        node.matches?.(
          '.nm-wishlist-button, .nm-PRODUCT_PAGE-wishlist-button, [class*="PRODUCT_PAGE-wishlist"]'
        )
      ) {
        return;
      }
      if (
        node.closest?.(
          '[data-pz-wishlist-launcher], [data-pz-wishlist-float-shell], .nm-wishlist-open-list-button, [class*="wishlist-open-list"], [class*="wishlist-float"]'
        )
      ) {
        return;
      }
      // Wishlist-only portals stay visible; notify-only portals get parked.
      if (
        node.matches?.('.nm-portal') &&
        (node.querySelector?.('.nm-wishlist-button, .nm-PRODUCT_PAGE-wishlist-button, [class*="PRODUCT_PAGE-wishlist"]') ||
          node.hasAttribute('data-pz-wishlist-rehomed'))
      ) {
        return;
      }

      node.setAttribute('data-pz-parked-notify-inject', 'true');
      for (const [prop, value] of Object.entries(parkProps)) {
        node.style.setProperty(prop, value, 'important');
      }

      const portal = node.closest?.('.nm-portal');
      if (
        portal instanceof HTMLElement &&
        portal !== node &&
        !portal.querySelector?.(
          '.nm-wishlist-button, .nm-PRODUCT_PAGE-wishlist-button, [class*="PRODUCT_PAGE-wishlist"]'
        )
      ) {
        portal.setAttribute('data-pz-parked-notify-inject', 'true');
        for (const [prop, value] of Object.entries(parkProps)) {
          portal.style.setProperty(prop, value, 'important');
        }
      }
    };

    // Class-based STOQ injects (incl. float shells).
    const selectors = [
      '.restock-rocket-button-product',
      '.restock-rocket-button',
      '.restock-rocket-button-collection',
      '.restock-rocket-preorder-button',
      '.restock-rocket-button-float',
      '.restock-rocket-button-container',
      '.restock-rocket-button-container-float-right',
      '.restock-rocket-button-container-float-left',
      '[class*="restock-rocket-button-container"]',
      '[class*="restock-rocket-button"]',
    ];
    for (const sel of selectors) {
      try {
        document.querySelectorAll(sel).forEach(applyPark);
      } catch (e) {
        /* ignore */
      }
    }

    // Label-based catch: app may inject a plain button into the buy box.
    try {
      document
        .querySelectorAll(
          '.product-form-buttons button, .product-form-buttons a, .product-form-buttons [role="button"], .buy-buttons-block button, .buy-buttons-block a'
        )
        .forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.('[data-app-proxy-cta], .app-proxy-cta')) return;
          const label = `${node.textContent || ''} ${node.getAttribute('aria-label') || ''}`;
          if (
            /notify(\s*me)?|restock|back\s*in\s*stock|pre-?\s?order|précommande|m['’]?\s*avert|me\s+notifier/i.test(
              label
            )
          ) {
            applyPark(node);
          }
        });
    } catch (e) {
      /* ignore */
    }
  }

  /** Refresh the proxy label when the app injects/updates its buttons late. */
  #observeAppButtons() {
    if (!('MutationObserver' in window)) return;
    // childList only — attribute/characterData on documentElement re-ran park on every
    // morph/class tweak and competed with variant interactions (INP).
    let timer = null;
    const schedule = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (document.hidden) return;
        this.#parkAppNotifyInjects();
        const mode = this.refs.appProxyCta?.dataset.mode;
        if (mode === 'pre_order' || mode === 'notify') this.#updateProxyButton(mode);
      }, 120);
    };

    const looksLikeAppInject = (node) => {
      if (!(node instanceof Element)) return false;
      const hay = `${node.className || ''} ${node.id || ''}`;
      if (/restock|notify|preorder|pre-order|nm-portal|stoq/i.test(hay)) return true;
      try {
        return Boolean(
          node.matches?.(
            '[class*="restock-rocket"], [data-notify-me], [data-action="notify"], [data-action="preorder"], .nm-portal'
          ) ||
            node.querySelector?.(
              '[class*="restock-rocket"], [data-notify-me], [data-action="notify"], [data-action="preorder"], .nm-portal'
            )
        );
      } catch {
        return false;
      }
    };

    this.#appButtonObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (looksLikeAppInject(node)) {
            schedule();
            return;
          }
        }
      }
    });
    this.#appButtonObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    // Initial park in case the app already injected.
    this.#parkAppNotifyInjects();
  }

  /**
   * Resolve the currently selected variant from the picker + variants config
   * (works before the section fetch returns).
   * @returns {object | null}
   */
  #resolveSelectedVariantFromPicker() {
    const section = this.closest('.shopify-section, .product-information') || document;
    const productId = this.dataset.productId || '';
    const picker =
      (productId && section.querySelector(`variant-picker[data-product-id="${productId}"]`)) ||
      section.querySelector('variant-picker');
    if (!picker) return null;

    const configScript = picker.querySelector('script[data-product-variants-config]');
    if (!configScript?.textContent) return null;

    let config;
    try {
      config = JSON.parse(configScript.textContent);
    } catch (e) {
      return null;
    }

    const variants = config?.variants;
    if (!Array.isArray(variants) || !variants.length) return null;

    // Match by selected option values first (Color + Size). Prefer this over
    // data-variant-id on the size radio — that id stays stale until morph/optimistic
    // updates and caused wrong Notify/ATC during color switches.
    const selectedValues = [];
    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked?.value) selectedValues.push(String(checked.value));
    });
    picker.querySelectorAll('select').forEach((select) => {
      if (select.value) selectedValues.push(String(select.value));
    });

    if (selectedValues.length) {
      const byOptions = variants.find((variant) => {
        const options = Array.isArray(variant.options)
          ? variant.options.map(String)
          : [variant.option1, variant.option2, variant.option3].filter(Boolean).map(String);
        if (options.length !== selectedValues.length) return false;
        return options.every((opt, index) => String(opt) === String(selectedValues[index]));
      });
      if (byOptions) return byOptions;
    }

    // Prefer explicit variant id on the latest selected control / URL
    const selectedControls = [
      ...picker.querySelectorAll('fieldset input[type="radio"]:checked'),
      ...picker.querySelectorAll('select option:checked'),
    ];
    for (let i = selectedControls.length - 1; i >= 0; i--) {
      const id = selectedControls[i]?.dataset?.variantId;
      if (id) {
        const match = variants.find((v) => String(v.id) === String(id));
        if (match) return match;
      }
    }

    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant) {
      const match = variants.find((v) => String(v.id) === String(urlVariant));
      if (match) return match;
    }

    return null;
  }

  /**
   * Apply the final purchase CTA immediately from local inventory JSON — same instant
   * path as the sticky bars. No pending blank window while Section Rendering fetches.
   */
  #applyOptimisticPurchaseState() {
    // Quick-add drawer keeps its own Sold out / Add UI (see quick-add.js).
    if (this.closest('quick-add-dialog, .quick-add-modal, #quick-add-modal-content')) {
      this.#applyOptimisticPreorderHide();
      return;
    }

    const selected = this.#resolveSelectedVariantFromPicker();
    const scope = this.closest('.shopify-section') || this;
    const purchaseState = selected
      ? getVariantPurchaseState(selected, scope)
      : { mode: 'unavailable', canAddToCart: false };

    const variantId = selected?.id;
    if (variantId && this.refs.variantId instanceof HTMLInputElement) {
      this.refs.variantId.value = String(variantId);
    }

    // Final CTA now — inventory is the source of truth (Add / Sold out).
    this.#syncAppCta(purchaseState, { pending: false });

    const { addToCartButtonContainer } = this.refs;
    const atcButton = addToCartButtonContainer?.refs?.addToCartButton;
    const isSoldOut =
      purchaseState.mode === 'sold_out' ||
      purchaseState.mode === 'pre_order' ||
      purchaseState.mode === 'notify';
    const unavailable = !selected || purchaseState.mode === 'unavailable';

    if (isSoldOut || unavailable || !purchaseState.canAddToCart) {
      addToCartButtonContainer?.disable?.();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      if (atcButton instanceof HTMLButtonElement) {
        const textEl =
          atcButton.querySelector('.add-to-cart-text__content') ||
          atcButton.querySelector('.add-to-cart-text');
        if (textEl) {
          textEl.textContent =
            purchaseState.mode === 'unavailable'
              ? Theme.translations?.unavailable || 'Unavailable'
              : Theme.translations?.sold_out || 'Sold out';
        }
      }
    } else {
      addToCartButtonContainer?.enable?.();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      if (atcButton instanceof HTMLButtonElement) {
        const textEl =
          atcButton.querySelector('.add-to-cart-text__content') ||
          atcButton.querySelector('.add-to-cart-text');
        if (textEl) {
          textEl.textContent = this.#getPurchaseButtonFallbackLabel(purchaseState);
        }
        this.#applyPurchaseButtonPresentation(atcButton, {
          mode: 'add_to_cart',
          canAddToCart: true,
        });
      }
    }

    this.querySelectorAll('.accelerated-checkout-block form input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement && variantId) {
        input.value = String(variantId);
      }
    });

    if (variantId) {
      this.#notifyAppOfVariantChange(variantId);
    }
  }

  /**
   * Legacy optimistic path (still used by quick-add). Prefer
   * `#applyOptimisticPurchaseState` on the PDP.
   */
  #applyOptimisticPreorderHide() {
    const selected = this.#resolveSelectedVariantFromPicker();
    const scope = this.closest('.shopify-section') || this;
    const purchaseState = selected
      ? getVariantPurchaseState(selected, scope)
      : { mode: 'unavailable', canAddToCart: false };

    const block = this.closest('.buy-buttons-block');
    const wasAppCta = block?.classList.contains('buy-buttons-block--app-cta');
    const willBePreorder = purchaseState.mode === 'pre_order';
    const willBeNotify = purchaseState.mode === 'sold_out';
    const willBeAppCta = willBePreorder || willBeNotify;

    if (willBeAppCta) {
      this.#syncAppCta(purchaseState, { pending: true });

      const variantId = selected?.id;
      if (variantId) {
        if (this.refs.variantId instanceof HTMLInputElement) {
          this.refs.variantId.value = String(variantId);
        }
        this.#notifyAppOfVariantChange(variantId);
      }
      return;
    }

    if (wasAppCta) {
      this.#syncAppCta(
        { mode: purchaseState.mode || 'add_to_cart', canAddToCart: Boolean(purchaseState.canAddToCart) },
        { pending: true }
      );
      return;
    }

    const variantId = selected?.id;
    if (variantId && this.refs.variantId instanceof HTMLInputElement) {
      this.refs.variantId.value = String(variantId);
    }
  }

  /**
   * Force Notify Me! / Restock Rocket to refresh its CTA for the selected variant.
   * Needed when switching preorder → preorder (app otherwise keeps a stale / hidden state).
   * @param {string | number} variantId
   */
  #notifyAppOfVariantChange(variantId) {
    if (!variantId) return;

    const input = this.refs.variantId;
    if (input instanceof HTMLInputElement) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const api =
      window._RestockRocket ||
      window.RestockRocket ||
      window.STOQ ||
      window._STOQ ||
      null;
    if (!api) return;

    try {
      if (typeof api.renderButtonForVariant === 'function') {
        api.renderButtonForVariant(String(variantId));
      }
      if (typeof api.updateVariant === 'function') {
        api.updateVariant(String(variantId));
      }
      if (typeof api.onVariantChange === 'function') {
        api.onVariantChange(String(variantId));
      }
      if (typeof api.refresh === 'function') {
        api.refresh();
      }
    } catch (e) {
      /* app API is best-effort */
    }
  }

  /**
   * @param {HTMLButtonElement} atcButton
   * @param {{ mode: string, canAddToCart?: boolean }} purchaseState
   */
  #applyPurchaseButtonPresentation(atcButton, purchaseState) {
    if (!(atcButton instanceof HTMLButtonElement)) return;

    const textEl =
      atcButton.querySelector('.add-to-cart-text__content') ||
      atcButton.querySelector('.add-to-cart-text');
    if (textEl) {
      textEl.textContent = this.#getPurchaseButtonFallbackLabel(purchaseState);
    }

    const canAdd = Boolean(purchaseState.canAddToCart) && purchaseState.mode === 'add_to_cart';
    let icon = atcButton.querySelector('.add-to-cart-icon');
    if (canAdd && !icon) {
      icon = document.createElement('span');
      icon.className = 'add-to-cart-icon';
      icon.setAttribute('aria-hidden', 'true');
      const textWrap = atcButton.querySelector('.add-to-cart-text') || atcButton;
      textWrap.insertAdjacentElement('beforebegin', icon);
    }
    if (icon instanceof HTMLElement) {
      icon.hidden = !canAdd;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    // Gallery-only color filter events have no HTML — ignore for purchase UI.
    if (event.detail?.data?.mediaOnly || !event.detail?.data?.html) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = this.#getMorphAddToCartSource(event.detail.data.html);

    if (!currentAddToCartButton) {
      return;
    }

    // Get the new variant ID
    const newVariantId = event.detail.resource?.id ?? '';

    // Enrich from variants config — morph payloads often omit inventory fields, which
    // incorrectly classifies preorder (continue + qty 0) as in-stock "Add to bag".
    const purchaseState = getVariantPurchaseState(
      event.detail.resource,
      this.closest('.shopify-section') || this
    );

    const isSoldOutCta =
      purchaseState.mode === 'sold_out' ||
      purchaseState.mode === 'pre_order' ||
      purchaseState.mode === 'notify';
    const variantUnavailable =
      event.detail.resource == null || purchaseState.mode === 'unavailable';

    // Apply Sold out / Add state before morph so wrong labels never flash.
    this.#syncAppCta(purchaseState, { pending: false });

    // Morph server-rendered button markup first, then apply purchase state so a stale
    // disabled attribute from the fetched HTML cannot leave in-stock variants grayed out.
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    } else {
      const textEl = currentAddToCartButton.querySelector('.add-to-cart-text__content');
      const iconEl = currentAddToCartButton.querySelector('.add-to-cart-icon');
      if (textEl) {
        textEl.textContent = this.#getPurchaseButtonFallbackLabel(purchaseState);
      }
      if (iconEl instanceof HTMLElement) {
        iconEl.hidden = !purchaseState.canAddToCart;
      }
    }

    // Re-apply after morph in case morph touched disabled/hidden attributes.
    this.#syncAppCta(purchaseState, { pending: false });
    // App may re-inject visible Notify/Pre-order nodes during morph — park once after settle.
    queueMicrotask(() => this.#parkAppNotifyInjects());

    if (isSoldOutCta || variantUnavailable || !purchaseState.canAddToCart) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      const liveButton = addToCartButtonContainer?.refs?.addToCartButton;
      if (liveButton instanceof HTMLButtonElement) {
        this.#applyPurchaseButtonPresentation(liveButton, purchaseState);
      }
    }

    if (this.closest('.quick-add-modal')) {
      queueMicrotask(() => {
        this.dispatchEvent(new CustomEvent('quick-add:purchase-refresh', { bubbles: true }));
      });
    }

    const morphedIcon = addToCartButtonContainer.refs.addToCartButton?.querySelector('.add-to-cart-icon');
    if (morphedIcon instanceof HTMLElement) {
      morphedIcon.hidden = !Boolean(purchaseState.canAddToCart) || purchaseState.mode !== 'add_to_cart';
    }

    // Update the variant ID
    if (!variantId) {
      return;
    }
    
    variantId.value = newVariantId;

    this.querySelectorAll('.accelerated-checkout-block form input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = newVariantId;
      }
    });

    this.#applyQuantityToForms(this.#resolveQuantityValue());

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Always ping the app after the id input is updated (preorder → preorder especially).
    this.#notifyAppOfVariantChange(newVariantId);
  };

  /**
   * Apply purchase CTA immediately on option click — before the section fetch returns.
   * Same inventory-first instant path as desktop/mobile sticky bars.
   */
  #onVariantSelected = () => {
    this.#applyOptimisticPurchaseState();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
